import 'client-only';

import { type FormEventHandler, useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useNavigation } from '@/components/flow/hooks/useNavigation';
import { type Brand } from '@/consts/brands';
import COOKIE_KEYS from '@/consts/cookieKeys';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { useRouter } from '@/hooks/useRouter';
import { saveRegistrationEligibility } from '@/services/eligibilityService/actions';
import { transitionOtcSignupUser } from '@/services/eligibilityService/client';
import { type IneligibleRegistrationEligibilityResponse } from '@/services/eligibilityService/types';
import { useTrackEligibility } from '@/services/eligibilityService/useEligibility';
import { type IneligibilityReasonType } from '@/types/eligibility/eligibility';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import cookieStorage from '@/utils/cookies/client';
import { urls } from '@/utils/urls';

import { logEligibilityFailure } from '../utils';
import {
  type EligibilityFormErrors,
  EligibilityFormSchema,
} from '../utils/validation';

type EligibilityFormOptions = {
  treatmentCategory: TreatmentCategory;
  brand: Brand;
};

export const useEligibilityForm = (options: EligibilityFormOptions) => {
  const { brand } = useFlowContext();
  const tracking = useTrackEligibility();
  const navigation = useNavigation();
  const [errors, setErrors] = useState<EligibilityFormErrors>();
  const router = useRouter();
  const [processing, setProcessing] = useState(false);

  const handleIneligibleResponse = (
    brand: Brand,
    response: IneligibleRegistrationEligibilityResponse,
  ) => {
    tracking.trackIneligibility(
      brand,
      response.ineligibleReason.reason as IneligibilityReasonType,
      {
        birthdate: new Date(response.birthdate || ''),
        state: response.state,
        termsAgreement: response.hasAcceptedTerms,
      },
    );

    router.push(urls.app.ineligible(options.treatmentCategory));
  };

  const submit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    tracking.trackEligibilitySubmission();

    const validated = EligibilityFormSchema.safeParse(
      Object.fromEntries(formData),
    );

    if (!validated.success) {
      setErrors(validated.error.flatten());
      return;
    }

    setProcessing(true);

    try {
      const response = await saveRegistrationEligibility(options.brand, {
        state: validated.data.state || '',
        zip: validated.data.zip || '',
        hasAcceptedTerms: validated.data.hasAcceptedTerms,
        birthdate: validated.data.birthdate,
        treatmentCategories: options.treatmentCategory,
      });

      if (response?.ineligibleReason) {
        handleIneligibleResponse(
          brand,
          response as IneligibleRegistrationEligibilityResponse,
        );

        return;
      }

      trackEvent(AnalyticsEvent.ELIGIBILITY_SUCCEEDED, {
        treatmentCategory: options.treatmentCategory,
        canConsentToMedicalTreatment:
          response?.canConsentToMedicalTreatment ?? undefined,
      });

      if (response?.isOtcSignupUser) {
        await transitionOtcSignupUser(options.brand, response);
      }

      const isOtcSignupUser = cookieStorage.get(COOKIE_KEYS.isOtcSignupUser);

      if (isOtcSignupUser) {
        router.push(urls[options.brand].legacy.auth.login);
      } else {
        navigation.onNext();
      }
    } catch (error: any) {
      logEligibilityFailure(error);
      setProcessing(false);
      return;
    }

    setProcessing(false);
  };

  return {
    submit,
    processing,
    errors,
  };
};
